import React, { useMemo, useState } from "react";
import { ListChecks, ChevronDown, AlertCircle, CalendarClock, CircleDashed, CalendarDays, CheckCircle2, Users } from "lucide-react";
import { useTodos, responsible, initialsOf } from "./todoUtils";
import { TodoItem } from "./TodoItem";
import { TodoQuickAdd } from "./TodoQuickAdd";

const BUCKETS = [
  { key: "overdue", label: "In ritardo",  color: "#DC2626", bg: "rgba(220,38,38,0.07)",   icon: AlertCircle },
  { key: "today",   label: "Oggi",        color: "#B7955B", bg: "rgba(183,149,91,0.09)",  icon: CalendarClock },
  { key: "nodate",  label: "Senza data",  color: "#64748B", bg: "rgba(100,116,139,0.06)", icon: CircleDashed },
  { key: "future",  label: "Prossimi",    color: "#0A192F", bg: "rgba(10,25,47,0.05)",    icon: CalendarDays },
];

function Bucket({ cfg, items, today, toggle, remove, update, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (items.length === 0) return null;
  const Icon = cfg.icon;
  return (
    <div data-testid={`todo-bucket-${cfg.key}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2 flex items-center gap-2 transition-colors hover:brightness-95"
        style={{ background: cfg.bg }}
      >
        <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} strokeWidth={2.4} />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: cfg.color }}>
          {cfg.label}
        </span>
        <span
          className="text-[10px] font-bold tabular-nums px-1.5 py-px rounded-full"
          style={{ background: "rgba(255,255,255,0.7)", color: cfg.color }}
        >
          {items.length}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: cfg.color, opacity: 0.6 }}
        />
      </button>
      {open && (
        <div className="divide-y divide-[#F0F2F5]">
          {items.map((t) => (
            <TodoItem key={t.id} t={t} today={today} onToggle={toggle} onDelete={remove} onUpdate={update} showClient />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Vista TEAM: raggruppata per responsabile (assegnatario o creatore) ── */
function TeamView({ items, today, toggle, remove, update }) {
  const members = useMemo(() => {
    const map = new Map();
    for (const t of items) {
      const doneToday = t.completed && (t.completedAt || "").slice(0, 10) === today;
      if (t.completed && !doneToday) continue; // mostra solo aperti + fatti oggi
      const r = responsible(t);
      const key = r.isMe ? "__me__" : r.name;
      if (!map.has(key)) {
        map.set(key, { key, name: r.name, isMe: r.isMe, open: [], doneToday: 0 });
      }
      const m = map.get(key);
      if (t.completed) m.doneToday += 1;
      else m.open.push(t);
    }
    return [...map.values()].sort((a, b) => (b.isMe - a.isMe) || (b.open.length - a.open.length));
  }, [items, today]);

  if (members.length === 0) {
    return (
      <div className="py-8 px-6 text-center" data-testid="todo-team-empty">
        <p className="text-[13px] text-slate-500">Nessun task attivo nel team oggi.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100" data-testid="todo-team-view">
      {members.map((m) => {
        const total = m.open.length + m.doneToday;
        const pct = total === 0 ? 0 : Math.round((m.doneToday / total) * 100);
        return (
          <div key={m.key} data-testid={`todo-team-member-${m.key}`}>
            {/* Header membro */}
            <div
              className="px-4 py-2.5 flex items-center gap-3"
              style={{ background: m.isMe ? "rgba(183,149,91,0.07)" : "rgba(10,25,47,0.03)" }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-serif text-[12px] shrink-0"
                style={m.isMe
                  ? { background: "#B7955B", color: "#fff" }
                  : { background: "#0A192F", color: "#D4AB6E" }}
              >
                {m.isMe ? "TU" : initialsOf(m.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-slate-800 truncate">
                  {m.name}
                  {m.isMe && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-[#8a6e3d]">i tuoi task</span>}
                </p>
                <p className="text-[10.5px] text-slate-500 tabular-nums">
                  {m.open.length === 0 ? "Tutto fatto 🎉" : `${m.open.length} da fare`} · {m.doneToday} {m.doneToday === 1 ? "fatta" : "fatte"} oggi
                </p>
              </div>
              {/* Progress membro */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-16 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: pct === 100 ? "#16A34A" : "linear-gradient(90deg, #B7955B, #D4AB6E)",
                      transition: "width 500ms cubic-bezier(0.16,1,0.3,1)",
                    }}
                  />
                </div>
                <span className="text-[10px] font-bold tabular-nums w-7 text-right" style={{ color: pct === 100 ? "#16A34A" : "#8a6e3d" }}>
                  {pct}%
                </span>
              </div>
            </div>
            {/* Task aperti del membro */}
            {m.open.length > 0 && (
              <div className="divide-y divide-[#F0F2F5]">
                {m.open.map((t) => (
                  <TodoItem key={t.id} t={t} today={today} onToggle={toggle} onDelete={remove} onUpdate={update} showClient />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function TodoDayPanel() {
  const { items, today, loading, add, toggle, remove, update } = useTodos(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("ba-todo-panel-view") || "due"; } catch { return "due"; }
  });
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("ba-todo-panel-collapsed") === "1"; } catch { return false; }
  });

  const setCollapsedPersist = (v) => {
    setCollapsed(v);
    try { localStorage.setItem("ba-todo-panel-collapsed", v ? "1" : "0"); } catch {}
  };
  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("ba-todo-panel-view", v); } catch {}
  };

  const visible = useMemo(
    () => (view === "due" && mineOnly ? items.filter((t) => responsible(t).isMe) : items),
    [items, mineOnly, view]
  );

  const buckets = useMemo(() => {
    const open = visible.filter((t) => !t.completed);
    return {
      overdue: open.filter((t) => t.due && t.due < today),
      today: open.filter((t) => t.due === today),
      nodate: open.filter((t) => !t.due),
      future: open.filter((t) => t.due && t.due > today),
      done: visible.filter((t) => t.completed).slice(0, 15),
    };
  }, [visible, today]);

  const todayScope = buckets.overdue.length + buckets.today.length + buckets.nodate.length;
  const doneCount = visible.filter((t) => t.completed && (t.completedAt || "").slice(0, 10) === today).length;
  const dayTotal = todayScope + doneCount;
  const pct = dayTotal === 0 ? 0 : Math.round((doneCount / dayTotal) * 100);

  return (
    <div
      className="bg-white shadow-[0_1px_3px_rgba(10,25,47,0.05)] overflow-hidden mb-5 animate-fade-in"
      style={{ border: "1px solid #E8ECF2", borderRadius: 10, borderTop: "3px solid #B7955B" }}
      data-testid="agenda-todo-panel"
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center gap-3 flex-wrap"
        style={{ background: "linear-gradient(135deg, rgba(183,149,91,0.08), rgba(212,171,110,0.03))" }}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(183,149,91,0.15)", border: "1px solid rgba(183,149,91,0.3)" }}>
          <ListChecks className="w-4 h-4 text-[#8a6e3d]" strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <h3 className="font-serif text-[15px] font-medium text-slate-900 leading-tight">To-Do del giorno</h3>
          <p className="text-[10.5px] text-slate-500 tabular-nums">
            {todayScope === 0 && doneCount > 0
              ? "Tutto completato — grande! 🎉"
              : `${todayScope} cose da fare · ${doneCount} fatte oggi`}
          </p>
        </div>
        {/* Progress giornaliero */}
        {dayTotal > 0 && (
          <div className="flex items-center gap-2 ml-2">
            <div className="w-24 h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background: "linear-gradient(90deg, #B7955B, #D4AB6E)",
                  transition: "width 600ms cubic-bezier(0.16,1,0.3,1)",
                }}
              />
            </div>
            <span className="text-[10.5px] font-bold text-[#8a6e3d] tabular-nums">{pct}%</span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {/* Switch vista: Scadenze / Team */}
          <div className="flex items-center p-0.5 rounded-full" style={{ background: "rgba(10,25,47,0.06)" }}>
            <button
              type="button"
              onClick={() => setViewPersist("due")}
              data-testid="todo-view-due"
              className="flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-semibold rounded-full transition-all"
              style={view === "due" ? { background: "#fff", color: "#0A192F", boxShadow: "0 1px 3px rgba(10,25,47,0.12)" } : { color: "#64748B" }}
            >
              <CalendarClock className="w-3 h-3" strokeWidth={2.4} /> Scadenze
            </button>
            <button
              type="button"
              onClick={() => setViewPersist("team")}
              data-testid="todo-view-team"
              className="flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-semibold rounded-full transition-all"
              style={view === "team" ? { background: "#0A192F", color: "#D4AB6E", boxShadow: "0 1px 3px rgba(10,25,47,0.25)" } : { color: "#64748B" }}
            >
              <Users className="w-3 h-3" strokeWidth={2.4} /> Team
            </button>
          </div>
          {/* Filtro Solo i miei (solo vista scadenze) */}
          {view === "due" && (
            <button
              type="button"
              onClick={() => setMineOnly((v) => !v)}
              data-testid="todo-mine-filter"
              className="px-2.5 py-1 text-[10.5px] font-semibold rounded-full transition-all"
              style={{
                background: mineOnly ? "#0A192F" : "#fff",
                color: mineOnly ? "#fff" : "#475569",
                border: mineOnly ? "1px solid #0A192F" : "1px solid #E2E8F0",
              }}
            >
              Solo i miei
            </button>
          )}
          <button
            type="button"
            onClick={() => setCollapsedPersist(!collapsed)}
            data-testid="todo-panel-collapse"
            title={collapsed ? "Espandi" : "Riduci"}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white transition-colors"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Quick add — default scadenza oggi */}
          <div className="px-4 py-3 bg-[#FAFBFC] border-y border-slate-100">
            <TodoQuickAdd onAdd={add} defaultDue={today} placeholder="Aggiungi una cosa da fare oggi…" />
          </div>

          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2].map((i) => <div key={i} className="skeleton h-10 w-full" />)}
            </div>
          ) : view === "team" ? (
            <TeamView items={items} today={today} toggle={toggle} remove={remove} update={update} />
          ) : visible.length === 0 ? (
            <div className="py-8 px-6 text-center" data-testid="todo-day-empty">
              <p className="text-[13px] text-slate-500">
                Nessun task. Scrivi qui sopra la prima cosa da fare della giornata.
              </p>
            </div>
          ) : (
            <div>
              <Bucket cfg={BUCKETS[0]} items={buckets.overdue} today={today} toggle={toggle} remove={remove} update={update} />
              <Bucket cfg={BUCKETS[1]} items={buckets.today} today={today} toggle={toggle} remove={remove} update={update} />
              <Bucket cfg={BUCKETS[2]} items={buckets.nodate} today={today} toggle={toggle} remove={remove} update={update} />
              <Bucket cfg={BUCKETS[3]} items={buckets.future} today={today} toggle={toggle} remove={remove} update={update} defaultOpen={false} />
              {buckets.done.length > 0 && (
                <div className="border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowDone((v) => !v)}
                    data-testid="todo-day-done-toggle"
                    className="w-full px-4 py-2 flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700/70 hover:text-emerald-700 hover:bg-emerald-50/50 transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Completati ({buckets.done.length})
                    <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${showDone ? "rotate-180" : ""}`} />
                  </button>
                  {showDone && (
                    <div className="divide-y divide-[#F0F2F5] animate-fade-in-fast">
                      {buckets.done.map((t) => (
                        <TodoItem key={t.id} t={t} today={today} onToggle={toggle} onDelete={remove} onUpdate={update} showClient />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
